import {
  Box,
  Button,
  FormLabel,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { uploadData } from "aws-amplify/storage";
import { format } from "date-fns";
import { Pencil, Trash } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import "swiper/css";
import AbsForm from "../../abstract/Form";
import CardWithHeader from "../../components/cards/CardWithHeader";
import ImageCropDialog from "../../components/ImageCropDialog";
import FileUploadButton from "../../components/inputs/FileUploadButton";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { FormProvider } from "../../contexts/FormContext";
import { colors, fontSizes } from "../../theme";
import { getBucketItemPath, getFileExtension } from "../../utils";
import { config } from "./config";
import {
  EmbryoCondition,
  EmbryoUpload,
  EmbryoUploadBase,
  EmbryoUploadImage,
  EmbryoUploadVideo,
  FormData,
  FormResponse,
  schema,
} from "./types";

import NoSelection from "../../assets/images/image-off.svg";
import JumpToSelection from "../../assets/images/fast-forward.svg";
import ClearSelection from "../../assets/images/x.svg";
import { ButtonSelectionInput, TextInput } from "../../components/inputs";

const maximumUploads = 16;

const minUploadWidth = 500;
const minUploadHeight = 500;

interface NullableFormData
  extends Omit<Omit<FormData, "fertilization">, "birth"> {
  birth: Date | null;
  transfer: Date | null;
  fertilization: string[];
}

const EmbryoQualityAssessmentForm: React.FC = () => {
  const { updateBreadcrumbs, translate } = useApp();
  const { showError, showSuccess } = useError();
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [uploads, setUploads] = useState<EmbryoUpload[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [cycleId, setCycleId] = useState<string | null>(null);
  const [croppingImage, setCroppingImage] = React.useState<string | null>(null);
  const [replacingIndex, setReplacingIndex] = React.useState<number | null>(
    null
  );

  const translations = useMemo(() => translate("embryo"), [translate]);
  const generalTranslations = useMemo(() => translate("general"), [translate]);

  const conditionOptions = Object.keys(EmbryoCondition).map((value) => ({
    value: value,
    label: translations.form.labels[value],
  }));

  const videoRefs = useRef<HTMLVideoElement[]>([]);

  const { apiService } = useApi();
  const [filesMap, setFilesMap] = React.useState<Map<string, File>>(new Map());
  const fileUploadButtonRef = useRef<HTMLButtonElement>(null);
  const [formData, setFormData] = useState<NullableFormData>({
    patient: "",
    birth: null,
    transfer: null,
    fertilization: [],
  });
  const { setSubmitting } = useApi();

  useEffect(() => {
    updateBreadcrumbs([
      {
        label: translations.breadcrumbs.embryoAssessment,
        onClick: () => {
          navigate("/embryo");
        },
      },
      {
        label: translations.breadcrumbs.newCycle,
      },
    ]);

    apiService.head("/embryo").catch((error) => {
      console.error("Error fetching embryo form", error);
    });
  }, []);

  const handleUploadReplace = (index: number) => {
    setReplacingIndex(index);
    fileUploadButtonRef.current?.click();
  };

  const handleUploadDelete = useCallback(
    (index: number) => {
      const upload = uploads[index!];
      const uploadsCopy = [...uploads];
      const filesMapCopy = new Map(filesMap);
      filesMapCopy.delete(upload.fileUrl);
      setFilesMap(filesMapCopy);
      uploadsCopy.splice(index!, 1);
      setUploads(uploadsCopy);
    },
    [uploads, filesMap]
  );

  const handleCropImage = (index: number) => {
    setCroppingImage(uploads[index].fileUrl);
  };

  const chooseCurrentFrame = (index: number) => {
    console.log("chooseCurrentFrame", index);
    const video = videoRefs.current[index];
    video.pause();
    console.log("video", video.currentTime);
    const thumbCanvas = document.createElement("canvas");
    const thumbContext = thumbCanvas.getContext("2d");
    thumbCanvas.width = video.videoWidth / 4;
    thumbCanvas.height = video.videoHeight / 4;
    thumbContext!.drawImage(video, 0, 0, thumbCanvas.width, thumbCanvas.height);
    const updatedUploads = [...uploads];
    const upload = updatedUploads[index] as EmbryoUploadVideo;
    upload.selectedTime = video.currentTime;
    upload.thumb = thumbCanvas.toDataURL("image/jpeg");
    setUploads(updatedUploads);
  };

  const seekCurrentFrame = (index: number) => {
    console.log("seekCurrentFrame", index);
    const video = videoRefs.current[index];
    const upload = uploads[index] as EmbryoUploadVideo;
    if (upload.selectedTime) {
      video.currentTime = upload.selectedTime;
    }
  };

  const clearCurrentFrame = (index: number) => {
    console.log("clearCurrentFrame", index);
    const updatedUploads = [...uploads];
    const upload = updatedUploads[index] as EmbryoUploadVideo;
    upload.selectedTime = null;
    upload.thumb = null;
    setUploads(updatedUploads);
  };

  const seekVideo = (index: number, event: any) => {
    console.log("seekVideo", index, event);
    const updatedUploads = [...uploads];
    const upload = updatedUploads[index] as EmbryoUploadVideo;
    upload.currentTime = event.target.currentTime;
    setUploads(updatedUploads);
  };

  const renderUploads = useCallback(() => {
    if (uploads.length === 0) {
      return <></>;
    }
    return (
      <Grid container spacing={2}>
        {uploads.map((upload, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <CardWithHeader headerColor="white" title={`Embryo ${index + 1}`}>
              <Stack width="100%" spacing={1} direction={"column"}>
                <Box position="absolute" top={12} right={12}>
                  <Stack direction="row" spacing={1}>
                    <Tooltip title={translations.form.upload.replace}>
                      <IconButton onClick={() => handleUploadReplace(index)}>
                        <Pencil width={24} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={translations.form.upload.delete}>
                      <IconButton onClick={() => handleUploadDelete(index)}>
                        <Trash width={24} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
                {/* <Box>
                  <TextInput
                    type="text"
                    label={translations.form.labels.label}
                    value={upload.label ?? ""}
                    onFocus={(ev) => {
                      ev.target.select();
                    }}
                    onChange={(value: string| number) => {
                      console.info("label", value);
                      setUploads([
                        ...uploads.slice(0, index),
                        {
                          ...upload,
                          label: value,
                        } as EmbryoUpload,
                        ...uploads.slice(index + 1),
                      ]);
                    }}
                  />
                </Box> */}
                <Box>
                  {upload.kind === "video" && (
                    <Box position="relative">
                      <video
                        controls
                        playsInline
                        width="100%"
                        src={upload.fileUrl}
                        ref={(el) => (videoRefs.current[index] = el!)}
                        onTimeUpdate={(event: any) => seekVideo(index, event)}
                      />
                      <Stack direction="column" spacing={1}>
                        <Button
                          disabled={
                            upload.selectedTime !== null &&
                            Math.abs(upload.currentTime - upload.selectedTime) <
                              0.1
                          }
                          color="greyButton"
                          onClick={() => chooseCurrentFrame(index)}
                        >
                          {upload.thumb
                            ? translations.form.labels.reselectFrame
                            : translations.form.labels.selectFrame}
                        </Button>
                        <Stack direction="column" spacing={0}>
                          <FormLabel
                            sx={{
                              fontWeight: "medium",
                              fontSize: fontSizes.sm,
                            }}
                          >
                            {translations.form.labels.selectedFrame}
                          </FormLabel>
                          <Stack direction="row" spacing={1}>
                            <img
                              width="92"
                              height="92"
                              src={upload.thumb ?? NoSelection}
                              alt={`Embryo ${index + 1}`}
                            />
                            {(upload.thumb && (
                              <Stack
                                direction="column"
                                spacing={1}
                                width="100%"
                                alignItems={"left"}
                              >
                                <Button
                                  color="whiteButton"
                                  onClick={() => seekCurrentFrame(index)}
                                  style={{ width: "100%" }}
                                >
                                  <img src={JumpToSelection} height={20} />
                                  <>&nbsp;&nbsp;</>
                                  <Typography fontSize={fontSizes.sm}>
                                    {translations.form.labels.jumpToFrame}
                                  </Typography>
                                </Button>
                                <Button
                                  color="whiteButton"
                                  onClick={() => clearCurrentFrame(index)}
                                  style={{ width: "100%" }}
                                >
                                  <img src={ClearSelection} height={20} />
                                  <>&nbsp;&nbsp;</>
                                  <Typography fontSize={fontSizes.sm}>
                                    {translations.form.labels.clearFrame}
                                  </Typography>
                                </Button>
                              </Stack>
                            )) || (
                              <Box>
                                <Typography
                                  fontSize={fontSizes.sm}
                                  color={colors.stone[500]}
                                  variant="subtitle2"
                                >
                                  {translations.form.labels.noFrame}
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </Box>
                <Box>
                  {upload.kind === "image" && (
                    <Box position="relative">
                      <img
                        width="100%"
                        src={upload.fileUrl}
                        alt={`Embryo ${index + 1}`}
                      />
                      <Button
                        color="greyButton"
                        sx={{ width: "100%" }}
                        onClick={() => handleCropImage(index)}
                      >
                        {translations.form.labels.cropFrame}
                      </Button>
                    </Box>
                  )}
                </Box>
                <Stack
                  direction="row"
                  spacing={2}
                  borderTop="1px solid #E7E5E4"
                >
                  <Box>
                    <ButtonSelectionInput
                      value={upload.condition ? [upload.condition] : []}
                      options={conditionOptions}
                      onChange={(value) => {
                        console.info(
                          "condition",
                          value,
                          new Array(upload.condition).toString()
                        );
                        if (value.length === 0) {
                          return;
                        }
                        setUploads([
                          ...uploads.slice(0, index),
                          {
                            ...upload,
                            condition: value[0] as EmbryoCondition,
                          },
                          ...uploads.slice(index + 1),
                        ]);
                      }}
                      label={translations.form.labels.condition}
                    />
                  </Box>
                  {(upload.kind === "image" ||
                    (upload.kind === "video" && upload.thumb)) && (
                    <Box>
                      <TextInput
                        type="float"
                        adornment={translations.form.labels.hours}
                        label={translations.form.labels.timePostInsemination}
                        value={upload.hpi}
                        onFocus={(ev) => {
                          ev.target.select();
                        }}
                        onChange={(value: number | string | null) => {
                          setUploads([
                            ...uploads.slice(0, index),
                            {
                              ...upload,
                              hpi: value,
                            } as EmbryoUpload,
                            ...uploads.slice(index + 1),
                          ]);
                          console.log("uploads", uploads.length);
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              </Stack>
            </CardWithHeader>
          </Grid>
        ))}
      </Grid>
    );
  }, [uploads]);

  const updateUpload = useCallback(
    (files: File[]) => {
      if (!files) {
        return false;
      }
      if (uploads.length + files.length > maximumUploads) {
        showError(
          translate("embryo", "form.errors.maxImagesExceeded", {
            maximum: `${maximumUploads}`,
          })
        );
        return false;
      }
      const uploadsCopy = [...uploads];
      const filesMapCopy = new Map(filesMap);

      if (replacingIndex !== null) {
        const upload = uploads[replacingIndex];
        const file = files[0];
        const url = URL.createObjectURL(file);
        filesMapCopy.set(url, file);
        uploadsCopy[replacingIndex] = {
          ...upload,
          fileUrl: url,
          originalName: file.name,
        } as EmbryoUploadImage;
        setUploads(uploadsCopy);
        setFilesMap(filesMapCopy);
        setReplacingIndex(null);
        if (fileUploadButtonRef.current?.value) {
          fileUploadButtonRef.current.value = "";
        }
        return;
      }

      for (const file of files) {
        const fileType = file.type.split("/")[0];
        const url = URL.createObjectURL(file);

        let uploadData: EmbryoUploadBase = {
          fileUrl: url,
          status: "init",
          label: "",
          originalName: file.name,
          hpi: null,
          condition: null,
        };
        if (fileType === "image") {
          uploadData = {
            ...uploadData,
            kind: "image",
          } as EmbryoUploadImage;
        } else if (fileType === "video") {
          uploadData = {
            ...uploadData,
            kind: "video",
            selectedTime: null,
            currentTime: 0,
            thumb: null,
          } as EmbryoUploadVideo;
        }
        uploadsCopy.push(uploadData! as EmbryoUpload);
        filesMapCopy.set(url, file);
        console.info("filesMap", filesMap);
      }
      setFilesMap(filesMapCopy);
      setUploads(uploadsCopy);
      if (fileUploadButtonRef.current?.value) {
        fileUploadButtonRef.current.value = "";
      }
    },
    [uploads, filesMap, user, replacingIndex, formData]
  );

  const getFormData = () => {
    const ret = {
      patient: formData.patient,
      ...(formData.birth && { birth: format(formData.birth, "yyyy-MM-dd") }),
      ...(formData.transfer && {
        transfer: format(formData.transfer, "yyyy-MM-dd"),
      }),
      ...(formData.fertilization?.length && {
        fertilization: parseInt(formData.fertilization[0]),
      }),
      files: uploads.length,
    };
    return ret;
  };

  const uploadMedia = async (embryoId: string) => {
    const uploadsCopy = [...uploads];
    console.log("uploads", uploadsCopy);
    for (let index = 0; index < uploadsCopy.length; index++) {
      try {
        const upload = uploadsCopy[index];
        const path = getBucketItemPath(
          user.clinic,
          "embryo",
          embryoId,
          index + 1,
          getFileExtension(upload.originalName)
        );
        const meta: any = {
          filename: upload.originalName,
        };
        if (upload.condition) {
          meta["condition"] =
            upload.condition === EmbryoCondition.frozen ? "1" : "0";
        }
        if (upload.hpi) {
          meta["hpi"] = parseFloat(upload.hpi.toString()).toFixed(2);
        }
        if (upload.label) {
          meta["label"] = upload.label;
        }
        if (upload.kind === "video" && upload.selectedTime) {
          meta["frame-time"] = upload.selectedTime.toFixed(2);
        }
        await uploadData({
          data: filesMap.get(upload.fileUrl)!,
          path: path,
          options: {
            metadata: meta,
          },
        });

        uploadsCopy[index].path = path;
        uploadsCopy[index].status = "uploaded";
      } catch (error) {
        console.error("Error uploading media", error);
        uploadsCopy[index].status = "error";
        showError(translations.form.messages.errorUploadingMedia);
      }
    }
    setUploads(uploadsCopy);
  };

  const validateImages = useCallback(() => {
    if (uploads.length === 0) {
      setUploadError(translations.form.messages.uploadsMissing);
      return false;
    }
    setUploadError(null);
    return true;
  }, [uploads]);

  const handleSubmit = useCallback(async () => {
    if (!validateImages()) {
      return false;
    }
    setSubmitting(true);
    try {
      const response = await apiService.put<FormResponse, FormData>(
        "/embryo",
        getFormData() as any
      );
      console.log("API Response:", response);

      setCycleId(response.cycle.id);

      await uploadMedia(response.cycle.id);

      navigate(`/embryo`, { state: { newId: response.cycle.id } });
      showSuccess(translations.form.messages.cycleCreated);
    } catch (error) {
      console.error("Error submitting form", error);
      showError(translations.form.messages.errorSubmittingForm);
    } finally {
      setSubmitting(false);
    }
  }, [uploads, filesMap, formData, getFormData]);

  const handleDataChange = (data: any) => {
    console.info("handleDataChange data", data);
    // updateFormData(data as FormData);
    setFormData(data);
  };

  return (
    <FormProvider>
      <Box margin={"auto"}>
        <Stack spacing={3} direction={"column"}>
          <AbsForm
            langCategory="embryo"
            system={user.config.weightHeightUnit ?? "metric"}
            initialValues={{}}
            config={config}
            onSubmit={handleSubmit}
            schema={schema}
            data={formData}
            onChange={handleDataChange}
            submitText={generalTranslations.form.buttons.save}
            uploads={uploads}
          />
          <CardWithHeader
            headerColor={"white"}
            title={translations.form.card.title}
          >
            <Stack spacing={2}>
              {renderUploads()}
              {uploadError && <Box color={"red"}>{uploadError}</Box>}
              <FileUploadButton
                minHeight={minUploadHeight}
                minWidth={minUploadWidth}
                onError={(file, error) => {
                  showError(
                    translate("general", error, {
                      file: file.name,
                      width: `${minUploadWidth}`,
                      height: `${minUploadHeight}`,
                    })
                  );
                }}
                title={translations.form.upload.title}
                caption={translations.form.upload.caption}
                ref={fileUploadButtonRef}
                accept="image/*,video/*"
                keepShowingUploadIcon={true}
                onChange={(files: File[]) => {
                  updateUpload(files);
                }}
              />
            </Stack>
          </CardWithHeader>
        </Stack>
      </Box>
      <ImageCropDialog
        open={!!croppingImage}
        onClose={() => setCroppingImage(null)}
        imageUrl={croppingImage ?? ""}
        onSave={(_croppedArea, croppedBlob: Blob) => {
          const uploadsCopy = [...uploads];
          const filesMapCopy = new Map(filesMap);
          const upload = uploadsCopy.find((u) => u.fileUrl === croppingImage);
          const index = uploadsCopy.indexOf(upload!);
          const file = new File([croppedBlob], upload!.originalName);
          const url = URL.createObjectURL(file);
          filesMapCopy.set(url, file);
          uploadsCopy[index] = {
            ...upload!,
            fileUrl: url,
          } as EmbryoUploadImage;
          setFilesMap(filesMapCopy);
          setUploads(uploadsCopy);
          setCroppingImage(null);
        }}
      />
    </FormProvider>
  );
};

export default EmbryoQualityAssessmentForm;
